import { useCallback, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

import { useEmbedParametersContext } from "../contexts/embedParametersContext";
import { usePlayerStore } from "../store/usePlayerStore";
import { AudioTrack } from "../types/common";
import { OndemandAudioTrack, Subtitles } from "../types/player-api";

import { useSendSnowplowEvent } from "./useSendSnowplowEvent";
import {
  findSelectedAudioTrack,
  getAudioTrackEventData,
  getStartMediaTrackingData,
  getSubtitlesEventData
} from "./useSnowplow-helpers";
import { useSnowplowEnabled } from "./useSnowplowEnabled";

const startsFromBeginning = (isLive: boolean, currentTime: number) =>
  !isLive && currentTime <= 0;

export const useSnowplow = () => {
  const { getState, useStoreState } = usePlayerStore();
  const setSnowplowEvents = useStoreState.setSnowplowEvents();
  const selectedAudioTrackId = useStoreState.selectedAudioTrackId();
  const audioTracks = useStoreState.audioTracks();
  const selectedSubtitles = useStoreState.selectedSubtitles();
  const programId = useStoreState.programId();

  const sessionId = useRef<string>();
  const latestCurrentTime = useRef<number>(Math.max(getState().currentTime, 0));
  const startedAt = useRef(Date.now());
  const prevSubtitles = useRef<Subtitles>();
  const prevSelectedAudioTrack = useRef<AudioTrack | OndemandAudioTrack>();

  const prevProgramId = useRef<string>();

  const embedParams = useEmbedParametersContext();

  const snowplowEnabled = useSnowplowEnabled();
  const sendEvent = useSendSnowplowEvent({ sessionId });

  // If the media is ondemand and starts from the beginning, no need to wait for initial seek
  // But if media is live or seek parameter is used
  // we need to wait for the player has been seeked to the correct starting position
  // before starting analytics session
  const initialSeekDone = useRef(
    startsFromBeginning(getState().isLive, getState().currentTime)
  );
  const firstPlayEventDone = useRef(false);

  const onTimeUpdate = useCallback(
    (currentTime: number) => {
      if (Math.round(currentTime) !== Math.round(latestCurrentTime.current)) {
        latestCurrentTime.current = currentTime;
        sendEvent("updateMediaTracking", {
          player: {
            currentTime
          }
        });
      }
    },
    [sendEvent]
  );

  const startMediaTracking = useCallback(() => {
    if (firstPlayEventDone.current && initialSeekDone.current) {
      sessionId.current = uuidv4();
      startedAt.current = Date.now();

      const state = getState();
      sendEvent(
        "startMediaTracking",
        getStartMediaTrackingData(latestCurrentTime.current, state, embedParams)
      );
    }
  }, [embedParams, getState, sendEvent]);

  const onPlay = useCallback(() => {
    sendEvent("trackMediaPlay");

    if (!firstPlayEventDone.current && !sessionId.current) {
      firstPlayEventDone.current = true;
      startMediaTracking();
    }
  }, [sendEvent, startMediaTracking]);

  const onSeekEnd = useCallback(() => {
    if (!initialSeekDone.current && !sessionId.current) {
      initialSeekDone.current = true;
      startMediaTracking();
    } else {
      sendEvent("trackMediaSeekEnd");
    }
  }, [sendEvent, startMediaTracking]);

  const onFinish = useCallback(() => {
    sendEvent("trackMediaEnd");
  }, [sendEvent]);

  const stopMediaTracking = useCallback(() => {
    if (sessionId.current) {
      onFinish();
      sendEvent("endMediaTracking");
      sessionId.current = undefined;
    }
  }, [onFinish, sendEvent]);

  useEffect(() => {
    if (programId && programId !== prevProgramId.current) {
      const { currentTime, isLive } = getState();
      stopMediaTracking();
      prevProgramId.current = programId;
      initialSeekDone.current = startsFromBeginning(isLive, currentTime);
      firstPlayEventDone.current = false;
      latestCurrentTime.current = Math.max(currentTime, 0);
      // The new media tracking will start after the first play event or after initial seek
    }
  }, [getState, programId, stopMediaTracking]);

  useEffect(() => {
    window.addEventListener("beforeunload", stopMediaTracking);

    return () => {
      window.removeEventListener("beforeunload", stopMediaTracking);
      stopMediaTracking();
    };
  }, [stopMediaTracking]);

  useEffect(() => {
    if (
      selectedSubtitles?.language !== prevSubtitles.current?.language ||
      selectedSubtitles?.label !== prevSubtitles.current?.label
    ) {
      prevSubtitles.current = selectedSubtitles;
      sendEvent(
        "trackMediaSelfDescribingEvent",
        getSubtitlesEventData(selectedSubtitles)
      );
    }
  }, [selectedSubtitles, sendEvent]);

  useEffect(() => {
    if (selectedAudioTrackId !== undefined) {
      const selectedAudioTrack = findSelectedAudioTrack(
        selectedAudioTrackId,
        audioTracks,
        getState().media?.audioTracks
      );

      if (
        selectedAudioTrack?.language !==
          prevSelectedAudioTrack.current?.language ||
        selectedAudioTrack?.type !== prevSelectedAudioTrack.current?.type
      ) {
        prevSelectedAudioTrack.current = selectedAudioTrack;
        sendEvent(
          "trackMediaSelfDescribingEvent",
          getAudioTrackEventData(selectedAudioTrack)
        );
      }
    }
  }, [audioTracks, getState, selectedAudioTrackId, sendEvent]);

  useEffect(() => {
    if (snowplowEnabled) {
      setSnowplowEvents({
        onBufferEnd: () => sendEvent("trackMediaBufferEnd"),
        onBufferStart: () => sendEvent("trackMediaBufferStart"),
        onDestroy: () => stopMediaTracking(),
        onFinish,
        onFullscreenChange: fullscreen =>
          sendEvent("trackMediaFullscreenChange", { fullscreen }),
        onPause: () => sendEvent("trackMediaPause"),
        onPipChange: pictureInPicture =>
          sendEvent("trackMediaPictureInPictureChange", { pictureInPicture }),
        onPlay,
        onPlaybackRateChange: newRate =>
          sendEvent("trackMediaPlaybackRateChange", {
            newRate: Math.max(0, newRate)
          }),
        onQualityChange: quality =>
          sendEvent("trackMediaQualityChange", quality),
        onSeekEnd: onSeekEnd,
        onSeekStart: () => sendEvent("trackMediaSeekStart"),
        onTimeUpdate: onTimeUpdate,
        onVolumeChange: newVolume =>
          sendEvent("trackMediaVolumeChange", {
            newVolume: Math.round(newVolume)
          })
      });
    }
  }, [
    onFinish,
    onPlay,
    onSeekEnd,
    onTimeUpdate,
    sendEvent,
    setSnowplowEvents,
    snowplowEnabled,
    stopMediaTracking
  ]);
};
